import React, { useState, useEffect } from 'react';

const FlamingoLoader = ({ message = "Setting up your fabulous project...", key }) => {
  const [danceVariant, setDanceVariant] = useState(() => Math.floor(Math.random() * 10));

  // Randomly select a new dance variant whenever the component is used
  useEffect(() => {
    setDanceVariant(Math.floor(Math.random() * 10));
  }, [key, message]);

  // Change dance every 30 seconds for variety during very long operations
  useEffect(() => {
    const interval = setInterval(() => {
      setDanceVariant(Math.floor(Math.random() * 10));
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getDanceClass = () => {
    switch(danceVariant) {
      case 0: return 'flamingo-walk';
      case 1: return 'flamingo-sway';
      case 2: return 'flamingo-spin';
      case 3: return 'flamingo-bounce';
      case 4: return 'flamingo-tilt';
      case 5: return 'flamingo-stretch';
      case 6: return 'flamingo-leg-lift';
      case 7: return 'flamingo-flap';
      case 8: return 'flamingo-hop';
      case 9: return 'flamingo-ballet';
      default: return 'flamingo-walk';
    }
  };

  const getDanceMessage = () => {
    const messages = [
      "Your flamingo assistant is strutting along perfectly!",
      "Swaying gracefully while organizing your project!",
      "Spinning with excitement for your new project!",
      "Bouncing with joy as everything comes together!",
      "Elegantly tilting to get the perfect angle!",
      "Stretching those flamingo skills to perfection!",
      "Lifting one leg like a true flamingo professional!",
      "Flapping with enthusiasm for your project!",
      "Hopping from task to task with flamingo flair!",
      "Performing a ballet of project perfection!"
    ];
    return messages[danceVariant] || messages[0];
  };
  return (
    <div className="flex flex-col items-center justify-center p-8">
      {/* Flamingo Animation Container */}
      <div className="relative w-32 h-32 mb-6">
        {/* Animated Flamingo SVG */}
        <svg
          className={`w-full h-full ${getDanceClass()}`}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Flamingo Body */}
          <ellipse
            cx="50"
            cy="55"
            rx="18"
            ry="25"
            fill="#FF69B4"
            className="flamingo-body"
          />
          
          {/* Flamingo Neck */}
          <path
            d="M 50 40 Q 45 25, 40 15"
            stroke="#FF69B4"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            className="flamingo-neck"
          />
          
          {/* Flamingo Head */}
          <circle
            cx="38"
            cy="12"
            r="8"
            fill="#FF69B4"
            className="flamingo-head"
          />
          
          {/* Flamingo Beak */}
          <path
            d="M 30 12 L 25 14 L 28 16 Z"
            fill="#333"
            className="flamingo-beak"
          />
          
          {/* Flamingo Eye */}
          <circle
            cx="36"
            cy="11"
            r="2"
            fill="white"
          />
          <circle
            cx="36"
            cy="11"
            r="1"
            fill="#333"
          />
          
          {/* Flamingo Legs */}
          <line
            x1="45"
            y1="75"
            x2="43"
            y2="95"
            stroke="#FF1493"
            strokeWidth="3"
            strokeLinecap="round"
            className="flamingo-leg-left"
          />
          <line
            x1="55"
            y1="75"
            x2="57"
            y2="95"
            stroke="#FF1493"
            strokeWidth="3"
            strokeLinecap="round"
            className="flamingo-leg-right"
          />
          
          {/* Feet */}
          <path
            d="M 43 95 L 40 97 M 43 95 L 46 97"
            stroke="#FF1493"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M 57 95 L 54 97 M 57 95 L 60 97"
            stroke="#FF1493"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-2 h-2 bg-pink-400 rounded-full sparkle-1"></div>
        <div className="absolute top-8 right-0 w-2 h-2 bg-pink-300 rounded-full sparkle-2"></div>
        <div className="absolute bottom-8 left-2 w-2 h-2 bg-pink-500 rounded-full sparkle-3"></div>
      </div>
      
      {/* Loading Text */}
      <h3 className="text-xl font-semibold text-pink-600 mb-2">
        {message}
      </h3>
      
      {/* Animated Dots */}
      <div className="flex space-x-2">
        <div className="w-2 h-2 bg-pink-400 rounded-full loading-dot-1"></div>
        <div className="w-2 h-2 bg-pink-400 rounded-full loading-dot-2"></div>
        <div className="w-2 h-2 bg-pink-400 rounded-full loading-dot-3"></div>
      </div>
      
      {/* Fun Messages */}
      <p className="mt-4 text-sm text-gray-600 italic flamingo-message">
        {getDanceMessage()}
      </p>
      
      {/* CSS Animations */}
      <style jsx>{`
        /* Dance Variation 0: Walking */
        @keyframes flamingoWalk {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(5px) rotate(-2deg); }
          50% { transform: translateX(0) rotate(0deg); }
          75% { transform: translateX(-5px) rotate(2deg); }
        }
        
        /* Dance Variation 1: Swaying */
        @keyframes flamingoSway {
          0%, 100% { transform: rotate(0deg) translateX(0); }
          25% { transform: rotate(-8deg) translateX(-3px); }
          50% { transform: rotate(0deg) translateX(0); }
          75% { transform: rotate(8deg) translateX(3px); }
        }
        
        /* Dance Variation 2: Spinning */
        @keyframes flamingoSpin {
          0% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(90deg) scale(0.9); }
          50% { transform: rotate(180deg) scale(0.8); }
          75% { transform: rotate(270deg) scale(0.9); }
          100% { transform: rotate(360deg) scale(1); }
        }
        
        /* Dance Variation 3: Bouncing */
        @keyframes flamingoBounce {
          0%, 100% { transform: translateY(0) scale(1, 1); }
          25% { transform: translateY(-10px) scale(1.1, 0.9); }
          50% { transform: translateY(-15px) scale(1.2, 0.8); }
          75% { transform: translateY(-5px) scale(1.1, 0.9); }
        }
        
        /* Dance Variation 4: Tilting */
        @keyframes flamingoTilt {
          0%, 100% { transform: rotate(0deg) translateY(0); }
          20% { transform: rotate(-15deg) translateY(-2px); }
          40% { transform: rotate(10deg) translateY(-1px); }
          60% { transform: rotate(-20deg) translateY(-3px); }
          80% { transform: rotate(15deg) translateY(-2px); }
        }
        
        /* Dance Variation 5: Stretching */
        @keyframes flamingoStretch {
          0%, 100% { transform: scaleY(1) translateY(0); }
          25% { transform: scaleY(1.2) translateY(-5px); }
          50% { transform: scaleY(1.3) translateY(-8px); }
          75% { transform: scaleY(1.1) translateY(-3px); }
        }
        
        /* Dance Variation 6: Leg Lifting */
        @keyframes flamingoLegLift {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(-2px) rotate(-5deg); }
          50% { transform: translateX(2px) rotate(5deg); }
          75% { transform: translateX(-1px) rotate(-3deg); }
        }
        
        /* Dance Variation 7: Flapping */
        @keyframes flamingoFlap {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.1) rotate(-3deg); }
          50% { transform: scale(1.2) rotate(0deg); }
          75% { transform: scale(1.1) rotate(3deg); }
        }
        
        /* Dance Variation 8: Hopping */
        @keyframes flamingoHop {
          0%, 100% { transform: translateY(0) translateX(0); }
          20% { transform: translateY(-8px) translateX(-3px); }
          40% { transform: translateY(-12px) translateX(2px); }
          60% { transform: translateY(-6px) translateX(-1px); }
          80% { transform: translateY(-10px) translateX(3px); }
        }
        
        /* Dance Variation 9: Ballet */
        @keyframes flamingoBallet {
          0%, 100% { transform: rotate(0deg) scale(1) translateY(0); }
          25% { transform: rotate(-10deg) scale(1.1) translateY(-3px); }
          50% { transform: rotate(0deg) scale(1.05) translateY(-8px); }
          75% { transform: rotate(10deg) scale(1.1) translateY(-3px); }
        }
        
        @keyframes legMove {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        
        @keyframes headBob {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-2px) rotate(-5deg); }
          75% { transform: translateY(2px) rotate(5deg); }
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        
        /* Dance Classes */
        .flamingo-walk { animation: flamingoWalk 2s ease-in-out infinite; }
        .flamingo-sway { animation: flamingoSway 3s ease-in-out infinite; }
        .flamingo-spin { animation: flamingoSpin 4s linear infinite; }
        .flamingo-bounce { animation: flamingoBounce 1.5s ease-in-out infinite; }
        .flamingo-tilt { animation: flamingoTilt 2.5s ease-in-out infinite; }
        .flamingo-stretch { animation: flamingoStretch 3s ease-in-out infinite; }
        .flamingo-leg-lift { animation: flamingoLegLift 2s ease-in-out infinite; }
        .flamingo-flap { animation: flamingoFlap 1.8s ease-in-out infinite; }
        .flamingo-hop { animation: flamingoHop 1.2s ease-in-out infinite; }
        .flamingo-ballet { animation: flamingoBallet 4s ease-in-out infinite; }
        
        .flamingo-body {
          transform-origin: center;
        }
        
        .flamingo-neck {
          animation: headBob 3s ease-in-out infinite;
          transform-origin: 50px 40px;
        }
        
        .flamingo-head {
          animation: headBob 3s ease-in-out infinite;
          transform-origin: 38px 12px;
        }
        
        .flamingo-beak {
          animation: headBob 3s ease-in-out infinite;
          transform-origin: 28px 14px;
        }
        
        .flamingo-leg-left {
          animation: legMove 1s ease-in-out infinite;
          transform-origin: 45px 75px;
        }
        
        .flamingo-leg-right {
          animation: legMove 1s ease-in-out infinite 0.5s;
          transform-origin: 55px 75px;
        }
        
        .sparkle-1 {
          animation: sparkle 2s ease-in-out infinite;
        }
        
        .sparkle-2 {
          animation: sparkle 2s ease-in-out infinite 0.7s;
        }
        
        .sparkle-3 {
          animation: sparkle 2s ease-in-out infinite 1.4s;
        }
        
        .loading-dot-1 {
          animation: dotPulse 1.5s ease-in-out infinite;
        }
        
        .loading-dot-2 {
          animation: dotPulse 1.5s ease-in-out infinite 0.2s;
        }
        
        .loading-dot-3 {
          animation: dotPulse 1.5s ease-in-out infinite 0.4s;
        }
        
        .flamingo-message {
          animation: fadeInOut 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default FlamingoLoader;